/**
 * Where the sandbox's two outbound buttons point — DATA ONLY.
 *
 * The demo languages and the pricing page. Editing what those buttons offer means
 * editing this file and nothing else; one file so nobody has to find out that
 * marketing URLs were scattered across components.
 *
 * 🔴 THESE MUST STAY `/book/<slug>` URLS ON THE APP HOST.
 * The dialog embeds them in an iframe, and `/book/` is the ONE path this product
 * deliberately allows to be framed cross-origin — the CloudFront viewer-response
 * function sends `frame-ancestors *` for it and nothing else, precisely because
 * the embeddable scheduler widget iframes booking pages onto customers' own
 * sites. Point an entry at any other path (say `/booking-success/...`, which
 * renders a real join link and is deliberately NOT framable) and the frame comes
 * back blank with only a console message to explain it.
 *
 * A FREE booking completes inside the frame: it reaches `/booking-success` by
 * client-side navigation, so no framing check ever runs on that step. These demo
 * links are free. A PAID event type would need to break out of the frame instead
 * — see `redirectToCheckout` in the scheduling code.
 */

export interface DemoOption {
  /** Stable id — used for the test hook and as the React key. */
  id: string;
  /** What the user picks. */
  label: string;
  /** One line under the label. */
  description: string;
  /** The public booking page for this option. */
  bookingUrl: string;
}

export const DEMO_OPTIONS: DemoOption[] = [
  {
    id: 'english-hindi',
    label: 'English / Hindi',
    description: 'A live walkthrough with our team, in English or Hindi.',
    bookingUrl: 'https://app.wenext.ai/book/english-hindi-demos',
  },
  {
    id: 'telugu',
    label: 'Telugu',
    description: 'The same walkthrough of the WeNext platform, in Telugu.',
    bookingUrl: 'https://app.wenext.ai/book/wenext-platform-walkthrough',
  },
];

/**
 * The Plans tab of the sandbox's own Billing & Subscription page.
 *
 * 🔴 AN IN-APP ROUTE, not the marketing site. It used to point at
 * `www.wenext.ai/pricing` because a sandbox visitor has no real account, so the
 * real authenticated `/home/billing` page looked like it would 404/empty-state
 * for them. That's no longer true here: the sandbox seeds a full fake session
 * (`web/src/sandbox/seed/core.ts` — `SANDBOX_USER` + `SANDBOX_SUBSCRIPTION`) and
 * a `plans` collection with a full Basic/Launch/Silver/Professional/Enterprise
 * catalog (`seed/core.ts` — `DISPLAY_PLAN_TIERS`), so the real Plans tab renders
 * correctly against seeded data. And because this is now a same-origin in-app
 * route, React Router navigates to it WITHOUT a full page reload — unlike the
 * external marketing site, this never destroys the IndexedDB-seeded session.
 */
export const PLANS_URL = '/home/billing?tab=plans';

/** Development-time sanity check, mirroring `validateTours`. Never throws. */
export function validateSandboxLinks(options: DemoOption[] = DEMO_OPTIONS): string[] {
  const problems: string[] = [];
  const ids = new Set<string>();

  if (!/^\/home\//.test(PLANS_URL)) {
    problems.push(`PLANS_URL is not an in-app /home/ route ("${PLANS_URL}")`);
  }

  for (const option of options) {
    if (ids.has(option.id)) problems.push(`duplicate demo option id "${option.id}"`);
    ids.add(option.id);

    // A booking page that cannot be framed renders as an empty dialog, so the
    // shape of the URL is worth checking where a developer will see it.
    if (!/^https:\/\/[^/]+\/book\/[^/?#]+/.test(option.bookingUrl)) {
      problems.push(
        `demo option "${option.id}" does not point at an https /book/<slug> page ` +
          `("${option.bookingUrl}") — see the note at the top of sandboxLinks.ts`,
      );
    }
  }

  return problems;
}
